import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import apiClient from '../../../utils/apiClient';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import { toast } from 'sonner';
import { 
  Fingerprint, 
  Shield, 
  Brain, 
  Sparkles, 
  ArrowRight,
  CheckCircle2,
  XCircle,
  Eye,
  Lock,
  RefreshCw,
  ChevronRight,
  ChevronLeft,
  User,
  MessageSquare
} from 'lucide-react';

/**
 * HumaTwin Personal Twin Pilot Onboarding
 * 
 * Purpose: Help new users understand the HumaTwin concept before starting formation.
 * 
 * Principles:
 * - Representation, not assistance
 * - Human sovereignty by design
 * - Progressive formation (Twin starts empty)
 * - No gamification of identity
 */

const ONBOARDING_STEPS = [
  { id: 'welcome', title: 'Welcome' },
  { id: 'what-is', title: 'What is HumaTwin' },
  { id: 'what-not', title: 'What it\'s NOT' },
  { id: 'how-it-works', title: 'How it Works' },
  { id: 'privacy', title: 'Your Control' },
  { id: 'start', title: 'Get Started' },
];

export default function PilotOnboarding() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [formationStatus, setFormationStatus] = useState(null);
  
  const isRTL = i18n.language === 'ar';

  useEffect(() => {
    // Check if user already has formation progress
    checkFormationStatus();
  }, []);

  const checkFormationStatus = async () => {
    try {
      const response = await apiClient.get('/api/identity-evidence/summary');
      setFormationStatus(response.data);
    } catch (error) {
      // New user, no formation yet
      setFormationStatus({ total_evidence_count: 0, confirmed_traits_count: 0 });
    }
  };

  const handleNext = () => {
    if (currentStep < ONBOARDING_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleStartIdentitySession = async () => {
    setLoading(true);
    try {
      // Mark onboarding as viewed
      await apiClient.post('/api/pilot/onboarding-complete');
      navigate('/personal/identity-formation');
    } catch (error) {
      // Continue anyway - onboarding tracking is not critical
      navigate('/personal/identity-formation');
    } finally {
      setLoading(false);
    }
  };

  const handleSkipToSession = async () => {
    try {
      await apiClient.post('/api/pilot/onboarding-complete');
    } catch (error) {
      // Non-critical
    }
    navigate('/personal/identity-formation');
  };

  const handleExplore = async () => {
    try {
      await apiClient.post('/api/pilot/onboarding-complete');
    } catch (error) {
      // Non-critical
    }
    navigate('/personal/dashboard');
  };

  return (
    <div 
      className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex flex-col"
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      {/* Progress Bar */}
      <div className="bg-white border-b border-slate-200 px-4 py-3">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-slate-500">
              {t('pilot.onboarding.step', { current: currentStep + 1, total: ONBOARDING_STEPS.length })}
            </span>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleExplore}
              className="text-slate-500 hover:text-slate-700"
              data-testid="skip-onboarding"
            >
              {t('pilot.onboarding.skipForNow')}
            </Button>
          </div>
          <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-slate-700 transition-all duration-300"
              style={{ width: `${((currentStep + 1) / ONBOARDING_STEPS.length) * 100}%` }}
            />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-4 sm:p-8">
        <div className="max-w-2xl w-full">
          {/* Step 0: Welcome */}
          {currentStep === 0 && (
            <div className="text-center space-y-8 animate-in fade-in duration-500" data-testid="onboarding-welcome">
              <div className="w-24 h-24 mx-auto bg-slate-900 rounded-3xl flex items-center justify-center">
                <Fingerprint className="w-12 h-12 text-white" />
              </div>
              
              <div className="space-y-4">
                <h1 className="text-4xl sm:text-5xl font-bold text-slate-900">
                  {t('pilot.onboarding.welcomeTitle', 'Welcome to HumaTwin')}
                </h1>
                <p className="text-xl text-slate-600 max-w-lg mx-auto">
                  {t('pilot.onboarding.welcomeSubtitle', 'A digital representation of how you think, prioritize, and decide')}
                </p>
              </div>

              <Card className="bg-white/80 backdrop-blur border-slate-200">
                <CardContent className="p-6">
                  <p className="text-slate-700 leading-relaxed">
                    {t('pilot.onboarding.welcomeDesc', 'HumaTwin gradually builds a digital representation of who you are — not to replace you, but to represent your perspective when you want it to.')}
                  </p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Step 1: What is HumaTwin */}
          {currentStep === 1 && (
            <div className="space-y-8 animate-in fade-in duration-500" data-testid="onboarding-what-is">
              <div className="text-center space-y-4">
                <div className="w-16 h-16 mx-auto bg-emerald-100 rounded-2xl flex items-center justify-center">
                  <Brain className="w-8 h-8 text-emerald-600" />
                </div>
                <h2 className="text-3xl font-bold text-slate-900">
                  {t('pilot.onboarding.whatIsTitle', 'What is HumaTwin?')}
                </h2>
              </div>

              <div className="grid gap-4">
                {[
                  {
                    icon: Fingerprint,
                    title: t('pilot.onboarding.whatIs1Title', 'Your Representation'),
                    desc: t('pilot.onboarding.whatIs1Desc', 'A digital representation of how you think, prioritize, and make decisions — built from what you share.')
                  },
                  {
                    icon: RefreshCw,
                    title: t('pilot.onboarding.whatIs2Title', 'Always Evolving'),
                    desc: t('pilot.onboarding.whatIs2Desc', 'Your Twin learns and changes as you do. Identity is never "complete" — it evolves over time.')
                  },
                  {
                    icon: Eye,
                    title: t('pilot.onboarding.whatIs3Title', 'Transparent'),
                    desc: t('pilot.onboarding.whatIs3Desc', 'You can always see what your Twin knows, where it came from, and correct it when it\'s wrong.')
                  },
                ].map((item, i) => {
                  const Icon = item.icon;
                  return (
                    <Card key={i} className="bg-white border-slate-200">
                      <CardContent className="p-5 flex items-start gap-4">
                        <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center flex-shrink-0">
                          <Icon className="w-5 h-5 text-slate-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-900">{item.title}</h3>
                          <p className="text-sm text-slate-600 mt-1">{item.desc}</p>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 2: What it's NOT */}
          {currentStep === 2 && (
            <div className="space-y-8 animate-in fade-in duration-500" data-testid="onboarding-what-not">
              <div className="text-center space-y-4">
                <div className="w-16 h-16 mx-auto bg-red-100 rounded-2xl flex items-center justify-center">
                  <XCircle className="w-8 h-8 text-red-600" />
                </div>
                <h2 className="text-3xl font-bold text-slate-900">
                  {t('pilot.onboarding.whatNotTitle', 'What HumaTwin is NOT')}
                </h2>
                <p className="text-slate-600">
                  {t('pilot.onboarding.whatNotSubtitle', 'Important to understand')}
                </p>
              </div>

              <div className="grid gap-3">
                {[
                  t('pilot.onboarding.whatNot1', 'Not a psychological diagnosis or personality test'),
                  t('pilot.onboarding.whatNot2', 'Not an authority over who you are'),
                  t('pilot.onboarding.whatNot3', 'Not an AI that silently decides your identity'),
                  t('pilot.onboarding.whatNot4', 'Not a replacement for you or your judgment'),
                  t('pilot.onboarding.whatNot5', 'Not omniscient — it only knows what you share'),
                ].map((item, i) => (
                  <Card key={i} className="bg-white border-slate-200">
                    <CardContent className="p-4 flex items-center gap-3">
                      <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                      <span className="text-slate-700">{item}</span>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <Card className="bg-amber-50 border-amber-200">
                <CardContent className="p-4">
                  <p className="text-amber-900 text-sm">
                    <strong>{t('pilot.onboarding.importantNote', 'Important')}:</strong> {t('pilot.onboarding.youDecide', 'You decide what becomes part of your identity. Observations are not confirmed identity until you choose to confirm them.')}
                  </p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Step 3: How it Works */}
          {currentStep === 3 && (
            <div className="space-y-8 animate-in fade-in duration-500" data-testid="onboarding-how-works">
              <div className="text-center space-y-4">
                <div className="w-16 h-16 mx-auto bg-blue-100 rounded-2xl flex items-center justify-center">
                  <Sparkles className="w-8 h-8 text-blue-600" />
                </div>
                <h2 className="text-3xl font-bold text-slate-900">
                  {t('pilot.onboarding.howWorksTitle', 'How Your Twin Forms')}
                </h2>
                <p className="text-slate-600">
                  {t('pilot.onboarding.howWorksSubtitle', 'Progressive formation — your Twin starts empty and grows with you')}
                </p>
              </div>

              <div className="relative">
                <div className="absolute top-5 bottom-5 start-5 w-0.5 bg-slate-200" />
                <div className="space-y-6">
                  {[
                    { 
                      step: '1', 
                      title: t('pilot.onboarding.step1Title', 'Identity Session'),
                      desc: t('pilot.onboarding.step1Desc', 'A conversation to explore how you think — not what you know.')
                    },
                    { 
                      step: '2', 
                      title: t('pilot.onboarding.step2Title', 'Observations'),
                      desc: t('pilot.onboarding.step2Desc', 'Your Twin observes patterns from reflections, decisions, and corrections.')
                    },
                    { 
                      step: '3', 
                      title: t('pilot.onboarding.step3Title', 'Emerging Patterns'),
                      desc: t('pilot.onboarding.step3Desc', 'Patterns emerge as evidence strengthens. Nothing is assumed.')
                    },
                    { 
                      step: '4', 
                      title: t('pilot.onboarding.step4Title', 'Your Confirmation'),
                      desc: t('pilot.onboarding.step4Desc', 'You decide which patterns represent you. Confirmation is always yours.')
                    },
                    { 
                      step: '5', 
                      title: t('pilot.onboarding.step5Title', 'Representation'),
                      desc: t('pilot.onboarding.step5Desc', 'With enough confirmed context, your Twin can represent your perspective.')
                    },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-4 relative">
                      <div className="w-10 h-10 bg-slate-900 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0 z-10">
                        {item.step}
                      </div>
                      <div className="pt-1">
                        <h3 className="font-semibold text-slate-900">{item.title}</h3>
                        <p className="text-sm text-slate-600 mt-1">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Privacy & Control */}
          {currentStep === 4 && (
            <div className="space-y-8 animate-in fade-in duration-500" data-testid="onboarding-privacy">
              <div className="text-center space-y-4">
                <div className="w-16 h-16 mx-auto bg-purple-100 rounded-2xl flex items-center justify-center">
                  <Shield className="w-8 h-8 text-purple-600" />
                </div>
                <h2 className="text-3xl font-bold text-slate-900">
                  {t('pilot.onboarding.privacyTitle', 'You Are Always in Control')}
                </h2>
              </div>

              <div className="grid gap-4">
                {[
                  {
                    icon: Eye,
                    title: t('pilot.onboarding.privacy1Title', 'See Everything'),
                    desc: t('pilot.onboarding.privacy1Desc', 'View what your Twin knows and where each piece of information came from.')
                  },
                  {
                    icon: RefreshCw,
                    title: t('pilot.onboarding.privacy2Title', 'Correct Anything'),
                    desc: t('pilot.onboarding.privacy2Desc', 'When your Twin gets something wrong, tell it. Your corrections matter.')
                  },
                  {
                    icon: Lock,
                    title: t('pilot.onboarding.privacy3Title', 'Export or Delete'),
                    desc: t('pilot.onboarding.privacy3Desc', 'Export your data anytime. Request full deletion when you want.')
                  },
                ].map((item, i) => {
                  const Icon = item.icon;
                  return (
                    <Card key={i} className="bg-white border-slate-200">
                      <CardContent className="p-5 flex items-start gap-4">
                        <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                          <Icon className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-900">{item.title}</h3>
                          <p className="text-sm text-slate-600 mt-1">{item.desc}</p>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              <Card className="bg-slate-100 border-slate-200">
                <CardContent className="p-4">
                  <p className="text-slate-700 text-sm">
                    <strong>{t('pilot.onboarding.aiNote', 'AI Processing')}:</strong> {t('pilot.onboarding.aiNoteDesc', 'Some content is processed by AI providers to understand patterns and generate representations. Your data is never sold or shared.')}
                  </p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Step 5: Get Started */}
          {currentStep === 5 && (
            <div className="text-center space-y-8 animate-in fade-in duration-500" data-testid="onboarding-start">
              <div className="w-24 h-24 mx-auto bg-emerald-100 rounded-3xl flex items-center justify-center">
                <CheckCircle2 className="w-12 h-12 text-emerald-600" />
              </div>
              
              <div className="space-y-4">
                <h2 className="text-3xl font-bold text-slate-900">
                  {t('pilot.onboarding.startTitle', 'Ready to Begin')}
                </h2>
                <p className="text-xl text-slate-600 max-w-lg mx-auto">
                  {t('pilot.onboarding.startSubtitle', 'Your Twin starts almost empty. Formation happens gradually, through genuine interaction.')}
                </p>
              </div>

              <Card className="bg-white border-slate-200">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 bg-slate-900 rounded-xl flex items-center justify-center">
                      <MessageSquare className="w-6 h-6 text-white" />
                    </div>
                    <div className="text-left">
                      <h3 className="font-semibold text-slate-900">
                        {t('pilot.onboarding.sessionTitle', 'Identity Session')}
                      </h3>
                      <p className="text-sm text-slate-600">
                        {t('pilot.onboarding.sessionDesc', 'A conversation to help your Twin begin understanding you')}
                      </p>
                    </div>
                  </div>
                  <Button 
                    onClick={handleStartIdentitySession} 
                    disabled={loading}
                    className="w-full bg-slate-900 hover:bg-slate-800"
                    size="lg"
                    data-testid="start-identity-session"
                  >
                    {loading ? (
                      t('common.loading', 'Loading...')
                    ) : (
                      <>
                        {t('pilot.onboarding.startSession', 'Start Identity Session')}
                        <ArrowRight className="w-5 h-5 ms-2" />
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>

              <p className="text-sm text-slate-500">
                {t('pilot.onboarding.laterNote', 'You can revisit this explanation anytime from Settings.')}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Navigation Footer */}
      <div className="bg-white border-t border-slate-200 px-4 py-4">
        <div className="max-w-2xl mx-auto flex justify-between items-center">
          <Button
            variant="ghost"
            onClick={handleBack}
            disabled={currentStep === 0}
            className="text-slate-600"
            data-testid="onboarding-back"
          >
            {isRTL ? <ChevronRight className="w-4 h-4 me-1" /> : <ChevronLeft className="w-4 h-4 me-1" />}
            {t('common.back', 'Back')}
          </Button>

          {currentStep < ONBOARDING_STEPS.length - 1 ? (
            <Button
              onClick={handleNext}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="onboarding-next"
            >
              {t('common.continue', 'Continue')}
              {isRTL ? <ChevronLeft className="w-4 h-4 ms-1" /> : <ChevronRight className="w-4 h-4 ms-1" />}
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
